import axios from "axios";
import { getCookieValue } from "../js/utils/cookies";

// API URL configuration based on environment
function resolveApiUrl() {
	if (import.meta.env.MODE === "production") {
		return (
			import.meta.env.VITE_PROD_API_URL || "https://goodglows.com/api/goodglows"
		);
	}
	if (import.meta.env.MODE === "staging") {
		return (
			import.meta.env.VITE_STAGING_API_URL ||
			"https://test.goodglows.com/api/goodglows"
		);
	}
	if (
		import.meta.env.MODE === "development" ||
		import.meta.env.MODE === "developmentstaging"
	) {
		if (import.meta.env.VITE_DEV_API_URL) {
			return import.meta.env.VITE_DEV_API_URL;
		}
	}
	if (import.meta.env.MODE === "local") {
		if (import.meta.env.VITE_LOCAL_API_URL) {
			return import.meta.env.VITE_LOCAL_API_URL;
		}
	}
	/** Optional API base for local dev / Playwright (e.g. http://127.0.0.1:1111/api) — does not override VITE_DEV_API_URL / VITE_LOCAL_API_URL above. */
	const localDevTestingUrl = import.meta.env.VITE_LOCAL_DEV_TESTING_API_URL;
	if (
		(import.meta.env.MODE === "development" ||
			import.meta.env.MODE === "developmentstaging" ||
			import.meta.env.MODE === "local") &&
		typeof localDevTestingUrl === "string" &&
		localDevTestingUrl.trim() !== ""
	) {
		return localDevTestingUrl.trim().replace(/\/$/, "");
	}
	if (typeof window !== "undefined") {
		const { hostname } = window.location;
		if (hostname !== "localhost" && hostname !== "127.0.0.1") {
			return `http://${hostname}:8000/api`;
		}
	}
	return "http://localhost:8000/api";
}

export const API_URL = resolveApiUrl();

/**
 * Absolute URL for the Goodglows SSE endpoint (EventSource cannot use axios baseURL).
 * Django exposes GET /api/goodglows/events/; VITE_* base may end in /api or /api/goodglows.
 */
export function getGoodglowsSseEventsUrl() {
	const base = String(API_URL || "").replace(/\/$/, "");
	if (base.endsWith("/goodglows")) {
		return `${base}/events/`;
	}
	return `${base}/goodglows/events/`;
}

// Image path configuration based on environment
const IMAGE_PATH =
	import.meta.env.MODE === "production" ||
	import.meta.env.MODE === "staging" ||
	import.meta.env.MODE === "developmentstaging"
		? import.meta.env.VITE_PROD_IMAGE_PATH || "/static/goodglows/images"
		: import.meta.env.MODE === "development"
			? import.meta.env.VITE_DEV_IMAGE_PATH || "/images"
			: import.meta.env.MODE === "local"
				? import.meta.env.VITE_LOCAL_IMAGE_PATH || "/images"
				: import.meta.env.VITE_DEV_IMAGE_PATH || "/images";

export const MEDIA_PATH =
	import.meta.env.MODE === "production"
		? import.meta.env.VITE_PROD_QR_BASE_URL
		: import.meta.env.MODE === "staging"
			? import.meta.env.VITE_STAGE_QR_BASE_URL
			: import.meta.env.MODE === "development"
				? import.meta.env.VITE_DEV_QR_BASE_URL
				: import.meta.env.VITE_DEV_QR_BASE_URL;

export const SUBTITLE_PATH =
	import.meta.env.MODE === "production" ||
	import.meta.env.MODE === "staging" ||
	import.meta.env.MODE === "developmentstaging"
		? import.meta.env.VITE_PROD_SUBTITLE_PATH || "/static/goodglows/subtitles"
		: import.meta.env.MODE === "development"
			? import.meta.env.VITE_DEV_SUBTITLE_PATH || "/subtitles"
			: import.meta.env.MODE === "local"
				? import.meta.env.VITE_LOCAL_SUBTITLE_PATH || "/subtitles"
				: import.meta.env.VITE_DEV_SUBTITLE_PATH || "/subtitles";

// SESSION_ID getter that dynamically retrieves session ID
export const SESSION_ID = () => {
	return (
		getCookieValue("goodglows_sessionid") ||
		localStorage.getItem("goodglows_sessionid")
	);
};

const VIDEO_PATH =
	import.meta.env.MODE === "production" ||
	import.meta.env.MODE === "staging" ||
	import.meta.env.MODE === "developmentstaging"
		? import.meta.env.VITE_PROD_VIDEO_PATH || "/static/goodglows/videos"
		: import.meta.env.MODE === "development"
			? import.meta.env.VITE_DEV_VIDEO_PATH || "/videos"
			: import.meta.env.MODE === "local"
				? import.meta.env.VITE_LOCAL_VIDEO_PATH || "/videos"
				: import.meta.env.VITE_DEV_VIDEO_PATH || "/videos";

// Configure axios instance
const apiClient = axios.create({
	baseURL: API_URL,
	withCredentials: true,
});

// Add request interceptor to dynamically set baseURL, CSRF token, and sessionid
apiClient.interceptors.request.use(
	(config) => {
		// Use API_URL for development (no env override) so baseURL is correct
		if (
			import.meta.env.MODE === "development" ||
			import.meta.env.MODE === "developmentstaging" ||
			import.meta.env.MODE === "local"
		) {
			if (
				!import.meta.env.VITE_DEV_API_URL &&
				!import.meta.env.VITE_LOCAL_API_URL &&
				!import.meta.env.VITE_LOCAL_DEV_TESTING_API_URL
			) {
				config.baseURL = API_URL;
				if (import.meta.env.DEV) {
					console.log(`[API] Using baseURL: ${API_URL}`);
				}
			}
		}

		const csrfToken = getCookieValue("csrftoken");
		if (csrfToken) {
			config.headers["X-CSRFToken"] = csrfToken;
		}

		// Include sessionid on every request (required for chat history and auth)
		const sessionid = SESSION_ID();
		if (sessionid) {
			config.params = { ...config.params, sessionid };
			// Only merge sessionid into body when it's a plain object (JSON). Do NOT overwrite FormData — that would drop ContestID, media, etc.
			if (
				config.data &&
				typeof config.data === "object" &&
				!Array.isArray(config.data) &&
				!(config.data instanceof FormData)
			) {
				config.data = { ...config.data, sessionid };
			}
		}

		return config;
	},
	(error) => {
		return Promise.reject(error);
	}
);

/** Base URL for adventure images (maps/panels). Matches production vs test hosts. */
export function getAdventureMediaBaseUrl() {
	if (import.meta.env.MODE === "production") {
		return "https://goodglows.com";
	}
	return "https://test.goodglows.com";
}

export { IMAGE_PATH, apiClient, VIDEO_PATH };
